export interface WhoMedicalSummary {
    summary: string;
    activeAlerts: string[];
    vaccinationGuidance: string[];
    lifeExpectancy?: { value: number | string; year?: number };
    universalHealthCoverage?: { value: number | string; year?: number };
    whoLink: string;
}

function stringList(value: unknown): string[] {
    if (Array.isArray(value))
        return value.map(item => String(item || '').trim()).filter(Boolean);
    if (typeof value === 'string' && value.trim())
        return [value.trim()];
    return [];
}

export function normalizeWhoMedicalSummary(source: any, country?: string): WhoMedicalSummary | null {
    if (!source)
        return null;

    const slug = String(country || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const whoLink = typeof source === 'object' && (source.whoLink || source.link)
        ? source.whoLink || source.link
        : slug ? `https://www.who.int/countries/${slug}/` : 'https://www.who.int/countries';

    if (typeof source === 'string')
        return {
            summary: source.trim(),
            activeAlerts: [],
            vaccinationGuidance: [],
            whoLink,
        };

    const summary = String(source.summary || source.overview || source.who_alerts || source.whoAlert || '').trim();
    const activeAlerts = stringList(source.activeAlerts || source.alerts || source.who_alerts || source.whoAlert);
    const vaccinationGuidance = stringList(source.vaccinationGuidance || source.vaccinations || source.vaccination_requirements);

    if (!summary && !activeAlerts.length && !vaccinationGuidance.length && !source.lifeExpectancy && !source.universalHealthCoverage)
        return null;

    return {
        summary: summary || 'Current medical intelligence is summarized below.',
        activeAlerts,
        vaccinationGuidance,
        lifeExpectancy: source.lifeExpectancy,
        universalHealthCoverage: source.universalHealthCoverage || source.uhcIndex,
        whoLink,
    };
}

export function mergeVerifiedWhoMedicalSummary(source: any, country: string, whoData: any): WhoMedicalSummary | null {
    const normalized = normalizeWhoMedicalSummary(source, country);
    const lifeExpectancy = whoData?.lifeExpectancy || normalized?.lifeExpectancy;
    const universalHealthCoverage = whoData?.universalHealthCoverage || normalized?.universalHealthCoverage;

    if (!normalized && !lifeExpectancy && !universalHealthCoverage)
        return null;

    const metricParts: string[] = [];
    if (lifeExpectancy)
        metricParts.push(`life expectancy of ${lifeExpectancy.value} years${lifeExpectancy.year ? ` (${lifeExpectancy.year})` : ''}`);
    if (universalHealthCoverage)
        metricParts.push(`a Universal Health Coverage index of ${universalHealthCoverage.value}${universalHealthCoverage.year ? ` (${universalHealthCoverage.year})` : ''}`);

    return {
        summary: normalized?.summary || `The latest available WHO baseline reports ${metricParts.join(' and ')}. These indicators describe population health and healthcare access capacity; current outbreak notices and traveller requirements should be verified through the linked WHO country guidance.`,
        activeAlerts: normalized?.activeAlerts || [],
        vaccinationGuidance: normalized?.vaccinationGuidance || [],
        lifeExpectancy,
        universalHealthCoverage,
        whoLink: normalized?.whoLink || normalizeWhoMedicalSummary('WHO country guidance.', country)!.whoLink,
    };
}
